import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigationDirection } from "../../navigation-direction-context/navigation-direction-content";
import { useBackController } from "../../back-controller/back-controller";
import { QuestionIdSuffixes } from "../../../models/enumerations/question-suffix-ids";
import SliderInputOption from "../../slider-input-option/slider-input-option";
import { Button } from "../../button/button";
import { FacingDirection } from "../../../models/enumerations/facing-directions";
import MskScoreUtil from "../../../utilities/msk-score-util";
import { createLogger } from "../../../utilities/logger";

const log = createLogger("MultiplePainScale");

const COMPONENT_CLASS = "c-multiple-pain-scale";

type PainArea = { name: string; facingDirection?: string };

export interface MultiplePainScaleProps {
    onChange: (newAnswer: string, forceAutoSubmit?: boolean) => void;
    painAreas: string; // e.g. '[{"Left Shoulder":"FRONT"},{"Right Shoulder":"BACK"}]' or "Neck, Wrist"
    answer?: string;   // e.g. '[{"area":"Left Shoulder","value":5,"direction":"FRONT"}]'
}

// ---- Helpers ---------------------------------------------------------------

// (area + direction) composite key
function makePainKey(name: string, direction?: string): string {
    return direction ? `${name}__${direction}` : name;
}

// Helper to parse the new JSON string format for painAreas (with fallback)
function parsePainAreas(input: string | undefined): PainArea[] {
    if (!input || !input.trim()) return [];

    // Try the new format: '[{"Right Shoulder":"BACK"},{"Left Shoulder":"FRONT"}]'
    try {
        const parsed = JSON.parse(input);
        if (Array.isArray(parsed)) {
            const result: PainArea[] = [];
            for (const item of parsed) {
                if (item && typeof item === "object") {
                    const [k, v] = Object.entries(item)[0] ?? [];
                    if (typeof k === "string" && typeof v === "string") {
                        result.push({ name: k, facingDirection: v });
                    }
                }
            }
            if (result.length > 0) return result;
        }
    } catch {
        // fall through to legacy parsing
    }

    // Fallback to legacy comma-separated string: "Neck, Wrist"
    return input
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((name) => ({ name }));
}

// Parse new answer format '[{"area":"Right Shoulder","value":55,"direction":"BACK"}]'
function parseAnswerToMap(answer: string | undefined): Record<string, number> {
    if (!answer || !answer.trim()) return {};

    try {
        const parsed = JSON.parse(answer);
        if (!Array.isArray(parsed)) return {};

        const map: Record<string, number> = {};
        for (const item of parsed) {
            if (!item || typeof item !== "object") continue;

            const area = String(item.area ?? "");
            const dir = item.direction ? String(item.direction) : undefined;
            const value = Number(item.value ?? NaN);

            if (area && !Number.isNaN(value)) {
                const key = makePainKey(area, dir); // <-- include direction in key
                map[key] = value;
            }
        }
        return map;
    } catch {
        log.warn("Invalid MultiplePainScale answer format, ignoring", {
            answer,
        });
        return {};
    }
}

function mapToJsonArrayString(
    values: Record<string, number>,
    areas: { name: string; facingDirection?: string }[]
): string {
    // Preserve order & include direction per area
    const arr = areas
        .map((a) => {
            const key = makePainKey(a.name, a.facingDirection);
            const value = values[key];
            if (value == null || Number.isNaN(value)) return null;

            return {
                area: a.name,
                value,
                direction: a.facingDirection ?? "",
            };
        })
        .filter((x): x is { area: string; value: number; direction: string } => x !== null);

    return JSON.stringify(arr);
}



const MultiplePainScale: React.FC<MultiplePainScaleProps> = (props) => {
    const areasArray = useMemo<PainArea[]>(
        () => parsePainAreas(props.painAreas),
        [props.painAreas]
    );
    const { t } = useTranslation();

    const { direction } = useNavigationDirection();
    const { push } = useBackController();

    // Parse previously saved JSON answer
    const savedValuesMap = useMemo(
        () => parseAnswerToMap(props.answer),
        [props.answer]
    );

    // Initialize state with any matching saved values
    const [valuesByArea, setValuesByArea] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};
        for (const area of areasArray) {
            const key = makePainKey(area.name, area.facingDirection);
            const existing = savedValuesMap[key];
            initial[key] = existing != null ? String(existing) : "";
        }
        return initial;
    });

    const [currentIndex, setCurrentIndex] = useState<number>(() =>
        direction === "backward" ? Math.max(areasArray.length - 1, 0) : 0
    );

    // Keep a ref for the current index so the back handler can read it
    const indexRef = useRef(currentIndex);
    useEffect(() => { indexRef.current = currentIndex; }, [currentIndex]);

    // Handle hardware/UI back button to navigate sliders backward
    useEffect(() => {
        const unregister = push(() => {
            if (indexRef.current > 0) {
                setCurrentIndex(prev => prev - 1);
                return true;
            }
            return false; // allow page-level back
        });
        return unregister;
    }, [push]);

    const currentArea = areasArray[currentIndex];
    const currentKey = makePainKey(currentArea.name, currentArea.facingDirection);

    // Get translated body part (pain area)
    const translatedArea = t(
        `api.questions.pain.${MskScoreUtil.toPainKey(
            currentArea.name,
            currentArea.facingDirection as FacingDirection
        )}`,
        { defaultValue: currentArea.name }
    );

    const handleSliderChange = (val: string) => {
        setValuesByArea(prev => ({ ...prev, [currentKey]: val }));
    };

    const handleNext = () => {
        const isLast = currentIndex === areasArray.length - 1;

        const visibleMap: Record<string, number> = {};
        for (const area of areasArray) {
            const key = makePainKey(area.name, area.facingDirection);
            const raw = valuesByArea[key];
            const num = raw == null || raw === "" ? undefined : parseFloat(raw);
            if (num != null && !Number.isNaN(num)) visibleMap[key] = num;
        }

        const jsonString = mapToJsonArrayString(visibleMap, areasArray);

        if (isLast) {
            props.onChange(jsonString, true);
        } else {
            props.onChange(jsonString);
            setCurrentIndex(prev => prev + 1);
        }
    };

    return (
        <div className={COMPONENT_CLASS}>
            <div className={`${COMPONENT_CLASS}__area`}>
                <label className={`${COMPONENT_CLASS}__label`}>
                    <Trans
                        i18nKey={`api.questions.movement.${QuestionIdSuffixes.PAIN_SCALE}.question_text`}
                        values={{ painArea: translatedArea }}
                        components={{
                            highlight: <span className="pain-area-highlight" />,
                        }}
                    />
                </label>

                <SliderInputOption
                    answer={valuesByArea[currentKey] ?? ""}
                    onChange={handleSliderChange}
                    disableInput={true}
                    enableTopRangeLabels={true}
                    bottomRangeLabelLeft={t('app.sliderLabels.noPain')}
                    bottomRangeLabelRight={t('app.sliderLabels.erTrip')}
                    gradient={true}
                    min={0}
                    max={10}
                    smoothFactor={10}
                />

                <Button
                    type="default"
                    buttonText={currentIndex === areasArray.length - 1 ? t('app.painScale.continue') : t('app.painScale.next')}
                    onClick={handleNext}
                    disabled={!valuesByArea[currentKey]}
                    inputClassName={`${COMPONENT_CLASS}__next-button`}
                />
            </div>
        </div>
    );
};

export default MultiplePainScale;
